import React, { useState, useEffect, useCallback } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import axios from 'axios';
import { Calendar as CalendarIcon, MapPin, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const groupEventsByMonth = (events) => {
    const grouped = {};
    events.forEach((event) => {
      const date = new Date(event.date);
      const monthYear = date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' });
      if (!grouped[monthYear]) {
        grouped[monthYear] = [];
      }
      grouped[monthYear].push(event);
    });
    return grouped;
  };

  const groupedEvents = groupEventsByMonth(events);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <section className="section-padding bg-white" data-testid="calendar-page">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h1 
              className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="calendar-title"
            >
              Calendrier des Activités
            </h1>
            <p className="text-base text-[#5C665A] max-w-2xl mx-auto leading-relaxed">
              Consultez le calendrier annuel de nos activités et événements.
            </p>
          </div>

          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A6B48]"></div>
            </div>
          )}
          {!loading && events.length === 0 && (
            <div className="text-center py-12" data-testid="no-events-message">
              <p className="text-[#5C665A]">Aucun événement prévu pour le moment.</p>
            </div>
          )}
          {!loading && events.length > 0 && (
            <div className="max-w-4xl mx-auto space-y-12">
              {Object.entries(groupedEvents).map(([monthYear, monthEvents]) => (
                <div key={monthYear} data-testid={`month-group-${monthYear}`}>
                  <h2 
                    className="text-2xl font-bold text-[#4A6B48] mb-6 capitalize"
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {monthYear}
                  </h2>
                  <div className="space-y-4">
                    {monthEvents.map((event) => (
                      <div 
                        key={event.event_id} 
                        className="card flex gap-6"
                        data-testid={`event-card-${event.event_id}`}
                      >
                        <div className="flex-shrink-0 text-center">
                          <div className="w-16 h-16 bg-[#4A6B48] text-white rounded-lg flex flex-col items-center justify-center">
                            <div className="text-2xl font-bold">
                              {new Date(event.date).getDate()}
                            </div>
                            <div className="text-xs uppercase">
                              {new Date(event.date).toLocaleDateString('fr-FR', { month: 'short' })}
                            </div>
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-medium text-[#D87D4A] px-2 py-1 bg-[#D87D4A]/10 rounded">
                              {event.category}
                            </span>
                          </div>
                          <h3 
                            className="text-xl font-bold text-[#2E332D] mb-2"
                            style={{ fontFamily: 'Cormorant Garamond, serif' }}
                          >
                            {event.title}
                          </h3>
                          <p className="text-sm text-[#5C665A] leading-relaxed mb-3">
                            {event.description}
                          </p>
                          <div className="flex items-center space-x-4 text-sm text-[#5C665A]">
                            <div className="flex items-center space-x-1">
                              <MapPin size={14} />
                              <span>{event.location}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
